import { TEX } from "../core/keys"
import { HUMANOID_MOVEMENT } from "../systems/enemies/humanoid-enemy"
import type { BallInteraction, BossAttackPattern, EnemyType } from "../types"

export type EnemyBodyConfig =
  | { shape: "circle"; radius: number; offsetX: number; offsetY: number }
  | {
      shape: "rect"
      width: number
      height: number
      offsetX: number
      offsetY: number
    }

export type EnemyMovementSpec =
  | { action: "track-player-y"; trackSpeed: number; stopOffsetPx?: number }
  | {
      action: "sine-by-last-shot"
      amplitude: number
      divisor: number
      stopOffsetPx?: number
    }
  | { action: "chase-player"; speed: number; trackSpeed: number }
  | {
      action: "panic-or-sine"
      fallbackAmplitude: number
      fallbackDivisor: number
      panicAmplitudeY: number
      panicDivisorY: number
      panicAmplitudeX: number
      panicDivisorX: number
      calmAmplitude: number
      calmDivisor: number
    }
  | {
      action: "sine"
      amplitude: number
      divisor: number
      stopOffsetPx?: number
    }
  | { action: "router-movement"; speed: number; rotateSpeedDeg: number }
  | {
      action: "router-star-bob"
      bobAmplitude: number
      bobPeriodMs: number
    }
  | {
      action: "stationary-mine"
      bobAmplitude: number
      bobPeriodMs: number
      driftSpeed: number
    }
  | { action: "harvester-patrol"; trackSpeed: number }
  | { action: "hovering" }
  | {
      action: "jetpack-human"
      /** Initial fly-in speed (px/frame) */
      approachSpeed: number
      /** Absolute X where the humanoid hovers (from left edge) */
      standoffX: number
      /** Vertical bob amplitude during hover */
      bobAmplitude: number
      /** Vertical bob period in ms */
      bobPeriodMs: number
      /** How quickly humanoid tracks player Y during hover */
      trackSpeed: number
      /** Lateral strafe speed toward player Y before shooting */
      strafeSpeed: number
      /** Speed when retreating after being hit */
      retreatSpeed: number
      /** How long the hit-retreat lasts in ms */
      retreatDurationMs: number
      /** Speed returning to standoff after retreat */
      returnSpeed: number
    }
  | { action: "router-movement"; speed: number; rotateSpeedDeg: number }
  | { action: "harvester-patrol"; trackSpeed: number }
  | {
      action: "serpentine-boss"
      swoopAmplitude: number
      swoopPeriodMs: number
      driftAmplitudeX: number
      driftPeriodMs: number
      trackSpeed: number
      secondaryWaveMultiplier: number
      secondaryWaveAmplitude: number
    }
  | {
      action: "snake-part"
      partRole: "head" | "body" | "tail"
      swoopAmplitude: number
      swoopPeriodMs: number
      attackRunSpeed: number
      attackRunDurationMs: number
      retreatDurationMs: number
      trackSpeed: number
    }
  | {
      action: "brain-boss"
      shieldedAmplitude: number
      shieldedPeriodMs: number
      exposedAmplitude: number
      exposedPeriodMs: number
      trackSpeed: number
      driftAmplitudeX: number
    }
  | {
      action: "kamikaze-dive"
      strikeSpeed: number
      loopBackSpeed: number
      trackSpeed: number
      reverseX: number
    }
  | {
      action: "moai-drift"
      /** Horizontal drift speed */
      speedX: number
      /** Gentle vertical drift amplitude */
      amplitudeY: number
    }
  | {
      action: "piranha-strike"
      chargeSpeed: number
      retreatSpeed: number
      trackSpeed: number
      chargeDelayMs: number
      attackCount: number
    }
  | {
      action: "hornet-pattern"
      speedX: number
      amplitudeY: number
      periodMs: number
      retreatDelayMs: number
    }
  | {
      action: "mine-climb"
      climbSpeed: number
      stopYOffset: number
      retreatSpeed: number
    }
  | {
      action: "mine-drop"
      damage: number
      mineCount: number
      mineSpreadY: number
      mineSpeed: number
    }

export type EnemyAttackSpec =
  | {
      action: "aimed-spread-shot"
      projectileCount?: number
      spreadDeg?: number
      damage: number
      projectileSpeedMultiplier?: number
    }
  | {
      action: "fixed-spread-shot"
      projectileCount?: number
      spreadDeg?: number
      damage: number
      projectileSpeedMultiplier?: number
      projectileTexture?: string
      muzzleFlashColor?: number
      projectileGlowColor?: number
      projectileGlowStrength?: number
    }
  | {
      action: "fireball-shot"
      damage: number
      projectileSpeedMultiplier?: number
      projectileTexture?: string
      muzzleFlashColor?: number
      projectileGlowColor?: number
      projectileGlowStrength?: number
    }
  | {
      action: "moai-ring-shot"
      damage: number
      projectileSpeed: number
      cooldown: number
    }
  | {
      action: "turret-tracking-shot"
      damage: number
      projectileSpeed: number
      /** Maximum angle of tracking deflection */
      maxAngleDeg: number
    }
  | {
      action: "aimed-laser-burst"
      damage: number
      burstCount: number
      burstDelayMs: number
    }
  | {
      action: "router-4way"
      damage: number
      rotateSpeedDeg: number
      projectileSpeedMultiplier?: number
    }
  | {
      action: "router-star-burst"
      damage: number
      patternCycle: string
      volleyCount: number
      burstDelayMs: number
      projectileSpeedMultiplier?: number
    }
  | {
      action: "mine-drop"
      damage: number
      mineCount: number
      mineSpreadY: number
      mineSpeed: number
    }

export interface BossPhaseTransitionSpec {
  phase: number
  minShootCooldown?: number
  shootCooldownMultiplier?: number
  ballInteraction?: BallInteraction
  bossAttackPattern?: BossAttackPattern
  supportGateActive?: boolean
  spawnAdds?: Array<{ type: EnemyType; count: number }>
  spawnBumperBurstCount?: number
  tintHex?: string
  clearTint?: boolean
}

export type EnemyBossPhaseSpec = {
  action: "wave-threshold-phases"
  initialBallInteraction?: BallInteraction
  bands: Array<{
    maxWave: number
    transitions: BossPhaseTransitionSpec[]
  }>
}

export interface EnemyAnimationSpec {
  key: string
  frames: number
  frameRate: number
  repeat?: number
  frameWidth: number
  frameHeight: number
}

export interface EnemyDefinition {
  type: EnemyType
  vx: number
  vy: number
  damage: number
  points: number
  shootCooldown: number
  color: string
  hpBase: number
  texture: string
  threatTier: number
  assetPath: string
  spriteSize: { w: number; h: number }
  body: EnemyBodyConfig
  movement: EnemyMovementSpec
  attack?: EnemyAttackSpec
  bossPhase?: EnemyBossPhaseSpec
  role: string
  movementSummary: string
  ballInteraction?: BallInteraction
  hoverDurationMs?: number
  animation?: EnemyAnimationSpec
  /** Maximum HP for boss root identification */
  bossMaxHp?: number
  /** Whether this is a destructible boss component */
  isBossPart?: boolean
  /** For composite bosses: list of parts to spawn relative to this parent */
  parts?: Array<{ type: EnemyType; offsetX: number; offsetY: number }>
}

// ── Enemy definitions ─────────────────────────────────────────

export const ENEMY_DEFINITIONS: Record<EnemyType, EnemyDefinition> = {
  orbDrone: {
    type: "orbDrone",
    vx: -3.5,
    vy: 0,
    damage: 8,
    points: 10,
    shootCooldown: 1400,
    color: "#67e8f9",
    hpBase: 20,
    texture: TEX.ENEMY_ORB_DRONE,
    threatTier: 1,
    assetPath: "enemies/orb-drone/orb-drone.webp",
    spriteSize: { w: 48, h: 48 },
    body: { shape: "circle", radius: 14, offsetX: 14, offsetY: 14 },
    movement: {
      action: "sine",
      amplitude: 12,
      divisor: 180,
      stopOffsetPx: 0,
    },
    role: "Swoop drone",
    movementSummary:
      "Flies in formation and swoops toward the player before exiting.",
    attack: {
      action: "fixed-spread-shot",
      projectileCount: 1,
      spreadDeg: 0,
      damage: 4,
      projectileSpeedMultiplier: 0.72,
    },
  },
  hornetDrone: {
    type: "hornetDrone",
    vx: -2.2,
    vy: 0,
    damage: 4,
    points: 15,
    shootCooldown: 0,
    color: "#facc15",
    hpBase: 20,
    texture: TEX.ENEMY_HORNET_DRONE,
    threatTier: 1,
    assetPath: "enemies/hornet-drone/hornet-drone-sheet.webp",
    spriteSize: { w: 66, h: 73 },
    animation: {
      key: "anim_hornet_drone",
      frames: 8,
      frameRate: 10,
      frameWidth: 128,
      frameHeight: 142,
      repeat: -1,
    },
    body: { shape: "circle", radius: 20, offsetX: 20, offsetY: 26 },
    movement: {
      action: "hornet-pattern",
      speedX: -2.5,
      amplitudeY: 40,
      periodMs: 600,
      retreatDelayMs: 800,
    },
    role: "Hornet",
    movementSummary: "Flies in figure-8 pattern and exits.",
  },
  mineDrone: {
    type: "mineDrone",
    vx: -1.5,
    vy: -0.8,
    damage: 30,
    points: 28,
    shootCooldown: 2000,
    color: "#a855f7",
    hpBase: 20,
    texture: TEX.ENEMY_MINE_DRONE_CLOSE,
    threatTier: 2,
    assetPath: "enemies/mine-drone/mine-drone-close.webp",
    spriteSize: { w: 96, h: 96 },
    body: { shape: "circle", radius: 36, offsetX: 24, offsetY: 18 },
    movement: {
      action: "mine-climb",
      climbSpeed: 1.2,
      stopYOffset: 120,
      retreatSpeed: -2.5,
    },
    attack: {
      action: "mine-drop",
      damage: 15,
      mineCount: 2,
      mineSpreadY: 60,
      mineSpeed: 2,
    },
    role: "Mine Layer",
    movementSummary:
      "Spawns at the top edge, glides left, and drops mines in bursts.",
  },
  scout: {
    type: "scout",
    vx: -5.5,
    vy: 0,
    damage: 6,
    points: 12,
    shootCooldown: 0,
    color: "#f472b6",
    hpBase: 16,
    texture: TEX.ENEMY_SCOUT,
    threatTier: 1,
    assetPath: "enemies/scout/scout-sheet.webp",
    spriteSize: { w: 64, h: 64 },
    animation: {
      key: "anim_scout",
      frames: 2,
      frameRate: 6,
      frameWidth: 512,
      frameHeight: 512,
      repeat: -1,
    },
    body: { shape: "rect", width: 45, height: 20, offsetX: 18, offsetY: 27 },
    movement: {
      action: "sine",
      amplitude: 8,
      divisor: 220,
      stopOffsetPx: 0,
    },
    role: "Scout",
    movementSummary: "Fast scout that zips across the screen.",
  },
  harvester: {
    type: "harvester",
    vx: -1.8,
    vy: 0,
    damage: 5,
    points: 16,
    shootCooldown: 0,
    color: "#FCD34D",
    hpBase: 28,
    texture: TEX.ENEMY_HARVESTER,
    threatTier: 1,
    assetPath: "enemies/harvester/harvester-drone.webp",
    spriteSize: { w: 64, h: 64 },
    body: { shape: "circle", radius: 28, offsetX: 8, offsetY: 8 },
    movement: { action: "harvester-patrol", trackSpeed: 0.8 },
    role: "Resource thief",
    movementSummary:
      "Patrols the back half of the screen, then darts in to steal drops before banking back to escort range.",
    ballInteraction: "catcher",
  },
  router: {
    type: "router",
    vx: -3,
    vy: 0,
    damage: 13,
    points: 18,
    shootCooldown: 900,
    color: "#8B5CF6",
    hpBase: 30,
    texture: TEX.ENEMY_ROUTER,
    threatTier: 2,
    assetPath: "enemies/router/router.webp",
    spriteSize: { w: 64, h: 64 },
    body: { shape: "rect", width: 60, height: 60, offsetX: 8, offsetY: 8 },
    movement: {
      action: "router-star-bob",
      bobAmplitude: 14,
      bobPeriodMs: 1200,
    },
    attack: {
      action: "router-star-burst",
      damage: 8,
      patternCycle: "plus-cross",
      volleyCount: 4,
      burstDelayMs: 0,
      projectileSpeedMultiplier: 0.72,
    },
    role: "Star Cannon",
    movementSummary:
      "Sweeps to center, fires rotating 4-way patterns alternating between + and X, then exits.",
  },
  spikeMine: {
    type: "spikeMine",
    vx: -0.8,
    vy: 0,
    damage: 18,
    points: 12,
    shootCooldown: 0,
    color: "#f97316",
    hpBase: 40,
    texture: TEX.ENEMY_SPIKE_MINE,
    threatTier: 1,
    assetPath: "enemies/spike-mine/spike-mine.webp",
    spriteSize: { w: 48, h: 48 },
    body: { shape: "circle", radius: 18, offsetX: 12, offsetY: 12 },
    movement: {
      action: "stationary-mine",
      bobAmplitude: 8,
      bobPeriodMs: 900,
      driftSpeed: 0.25,
    },
    role: "Lane Denier",
    movementSummary:
      "A deployed hazard that hovers in place and punishes careless lane movement.",
  },
  healer: {
    type: "healer",
    vx: -1.1,
    vy: 0,
    damage: 0,
    points: 24,
    shootCooldown: 0,
    color: "#22D3EE",
    hpBase: 32,
    texture: TEX.ENEMY_HEALER,
    threatTier: 2,
    assetPath: "enemies/healer/healer.webp",
    spriteSize: { w: 64, h: 64 },
    body: { shape: "circle", radius: 34, offsetX: 11, offsetY: 11 },
    movement: {
      action: "panic-or-sine",
      fallbackAmplitude: 1.2,
      fallbackDivisor: 150,
      panicAmplitudeY: 4,
      panicDivisorY: 30,
      panicAmplitudeX: 2,
      panicDivisorX: 25,
      calmAmplitude: 0.8,
      calmDivisor: 150,
    },
    role: "Support",
    movementSummary: "Escort drone that holds behind the front line.",
  },
  kamikaze: {
    type: "kamikaze",
    vx: -5,
    vy: 0,
    damage: 30,
    points: 25,
    shootCooldown: 0,
    color: "#F87171",
    hpBase: 12,
    texture: TEX.ENEMY_KAMIKAZE,
    threatTier: 2,
    assetPath: "enemies/kamikaze/kamikaze-sheet.webp",
    spriteSize: { w: 86, h: 60 },
    animation: {
      key: "anim_kamikaze",
      frames: 4,
      frameRate: 10,
      frameWidth: 1024,
      frameHeight: 712,
      repeat: -1,
    },
    body: { shape: "rect", width: 120, height: 38, offsetX: 8, offsetY: 14 },
    movement: {
      action: "kamikaze-dive",
      trackSpeed: 0.18,
      strikeSpeed: 6,
      loopBackSpeed: 3,
      reverseX: 20,
    },
    role: "Suicide Bomber",
    movementSummary: "Charges hard at the player.",
  },
  gunPod: {
    type: "gunPod",
    vx: -1.0,
    vy: 0,
    damage: 12,
    points: 18,
    shootCooldown: 1100,
    color: "#a78bfa",
    hpBase: 22,
    texture: TEX.ENEMY_GUN_POD,
    threatTier: 2,
    assetPath: "enemies/gun-pod/gun-pod.webp",
    spriteSize: { w: 64, h: 64 },
    body: { shape: "circle", radius: 20, offsetX: 12, offsetY: 12 },
    movement: { action: "track-player-y", trackSpeed: 0.15, stopOffsetPx: 260 },
    attack: { action: "router-4way", damage: 8, rotateSpeedDeg: 45 },
    role: "Gun Pod",
    movementSummary: "Locks a lane for a short burst pattern.",
  },
  bioPod: {
    type: "bioPod",
    vx: -0.6,
    vy: 0,
    damage: 12,
    points: 24,
    shootCooldown: 1600,
    color: "#8b5cf6",
    hpBase: 22,
    texture: TEX.ENEMY_BIO_POD,
    threatTier: 2,
    assetPath: "enemies/bio-pod/bio-pod.webp",
    spriteSize: { w: 64, h: 64 },
    body: { shape: "circle", radius: 28, offsetX: 10, offsetY: 12 },
    movement: {
      action: "sine",
      amplitude: 14,
      divisor: 260,
      stopOffsetPx: 260,
    },
    attack: {
      action: "fireball-shot",
      damage: 6,
      projectileSpeedMultiplier: 1.05,
      projectileGlowColor: 0xa855f7,
    },
    role: "Bio Pod",
    movementSummary: "Organic pod that spits a stronger shot pattern.",
  },
  chainDrifter: {
    type: "chainDrifter",
    vx: -1.4,
    vy: 0,
    damage: 15,
    points: 35,
    shootCooldown: 1800,
    color: "#fb7185",
    hpBase: 45,
    texture: TEX.ENEMY_CHAIN_DRIFTER,
    threatTier: 3,
    assetPath: "enemies/chain-drifter/chain-drifter-sheet.webp",
    spriteSize: { w: 80, h: 80 },
    animation: {
      key: "anim_chain_drifter",
      frames: 8,
      frameRate: 10,
      repeat: -1,
      frameWidth: 1024,
      frameHeight: 1024,
    },
    body: { shape: "circle", radius: 25, offsetX: 9, offsetY: 23 },
    movement: {
      action: "sine",
      amplitude: 24,
      divisor: 140,
    },
    attack: {
      action: "fixed-spread-shot",
      projectileCount: 3,
      spreadDeg: 12,
      damage: 10,
    },
    role: "Chain Link",
    movementSummary:
      "Snakes vertically while firing only forward; dodge by changing lanes before the volley crosses.",
  },
  neonMoai: {
    type: "neonMoai",
    vx: -0.6,
    vy: 0,
    damage: 25,
    points: 60,
    shootCooldown: 2200,
    color: "#38bdf8",
    hpBase: 120,
    texture: TEX.ENEMY_NEON_MOAI,
    threatTier: 4,
    assetPath: "enemies/neon-moai/neon-moai.webp",
    spriteSize: { w: 96, h: 128 },
    body: { shape: "rect", width: 60, height: 90, offsetX: 18, offsetY: 10 },
    movement: { action: "moai-drift", speedX: -0.5, amplitudeY: 10 },
    attack: {
      action: "moai-ring-shot",
      damage: 15,
      projectileSpeed: 4.5,
      cooldown: 2200,
    },
    role: "Neon Monolith",
    movementSummary: "Slow heavy platform that emits expanding rings.",
  },
  dwarf: {
    type: "dwarf",
    vx: -1.2,
    vy: 0,
    damage: 6,
    points: 30,
    shootCooldown: 700,
    color: "#f59e0b",
    hpBase: 40,
    texture: TEX.ENEMY_DWARF,
    threatTier: 2,
    assetPath: "enemies/dwarf/dwarf-sheet.webp",
    spriteSize: { w: 48, h: 48 },
    body: { shape: "circle", radius: 18, offsetX: 6, offsetY: 6 },
    animation: {
      key: "anim_dwarf",
      frames: 4,
      frameRate: 12,
      frameWidth: 1004,
      frameHeight: 1024,
      repeat: -1,
    },
    movement: { action: "track-player-y", trackSpeed: 0.6 },
    attack: {
      action: "aimed-laser-burst",
      damage: 5,
      burstCount: 2,
      burstDelayMs: 200,
    },
    role: "Escort Sentry",
    movementSummary: "Compact turret that holds your vertical line.",
  },
  bomber: {
    type: "bomber",
    vx: -2.8,
    vy: 0,
    damage: 22,
    points: 40,
    shootCooldown: 1200,
    color: "#fb923c",
    hpBase: 70,
    texture: TEX.ENEMY_BOMBER,
    threatTier: 3,
    assetPath: "enemies/bomber/bomber.webp",
    spriteSize: { w: 96, h: 72 },
    body: { shape: "rect", width: 80, height: 40, offsetX: 8, offsetY: 15 },
    movement: {
      action: "sine",
      amplitude: 25,
      divisor: 120,
      stopOffsetPx: 180,
    },
    role: "Heavy Bomber",
    movementSummary: "Fast strike craft that drops heavy payloads.",
    attack: {
      action: "fireball-shot",
      damage: 15,
      projectileSpeedMultiplier: 1.3,
    },
  },
  piranha: {
    type: "piranha",
    vx: -6,
    vy: 0,
    damage: 25,
    points: 45,
    shootCooldown: 0,
    color: "#ef4444",
    hpBase: 28,
    texture: TEX.ENEMY_PIRANHA,
    threatTier: 2,
    assetPath: "enemies/piranha/piranha-sheet.webp",
    spriteSize: { w: 88, h: 76 },
    animation: {
      key: "anim_piranha",
      frames: 4,
      frameRate: 12,
      frameWidth: 272.5,
      frameHeight: 236,
      repeat: -1,
    },
    body: { shape: "rect", width: 92, height: 44, offsetX: 10, offsetY: 24 },
    movement: {
      action: "piranha-strike",
      chargeSpeed: 14,
      retreatSpeed: 8,
      trackSpeed: 0.25,
      chargeDelayMs: 400,
      attackCount: 3,
    },
    role: "Piranha",
    movementSummary: "Swims in, locks on, charges wildly, then retreats.",
  },
  // BOSSES
  humanoid: {
    type: "humanoid",
    vx: -2.4,
    vy: 0,
    damage: 6,
    points: 42,
    shootCooldown: 1800,
    color: "#F472B6",
    hpBase: 500,
    texture: TEX.BOSS_HUMANOID,
    threatTier: 3,
    assetPath: "enemies/boss-humanoid/idle-sheet.webp",
    spriteSize: { w: 128, h: 128 },
    animation: {
      key: "anim_boss_humanoid_idle",
      frames: 8,
      frameRate: 8,
      frameWidth: 1024,
      frameHeight: 1024,
      repeat: -1,
    },
    body: { shape: "rect", width: 64, height: 100, offsetX: 32, offsetY: 14 },
    movement: HUMANOID_MOVEMENT,
    role: "APEX Soldier",
    movementSummary:
      "An elite jetpack pilot that hovers at standoff range, using laser-sight tracking to line up high-damage shots.",
    ballInteraction: "normal",
    bossMaxHp: 500,
    attack: {
      action: "fixed-spread-shot",
      projectileCount: 1,
      spreadDeg: 0,
      damage: 6,
      projectileTexture: TEX.PROJ_SPARK,
      muzzleFlashColor: 0xfacc15,
      projectileGlowColor: 0xf97316,
      projectileGlowStrength: 8,
    },
    bossPhase: {
      action: "wave-threshold-phases",
      initialBallInteraction: "normal",
      bands: [
        {
          maxWave: 5,
          transitions: [
            {
              phase: 2,
              minShootCooldown: 1000,
              shootCooldownMultiplier: 0.7,
              ballInteraction: "armored",
              bossAttackPattern: "pressure",
              spawnAdds: [{ type: "dwarf", count: 2 }],
              spawnBumperBurstCount: 1,
            },
            {
              phase: 3,
              minShootCooldown: 600,
              shootCooldownMultiplier: 0.5,
              ballInteraction: "normal",
              bossAttackPattern: "climax",
              spawnAdds: [{ type: "dwarf", count: 3 }],
              spawnBumperBurstCount: 2,
            },
          ],
        },
      ],
    },
  },
  snake: {
    type: "snake",
    vx: 0,
    vy: 0,
    damage: 45,
    points: 250,
    shootCooldown: 1200,
    color: "#DC2626",
    hpBase: 600,
    texture: TEX.BOSS_SNAKE,
    threatTier: 5,
    assetPath: "enemies/boss-snake/fly-sheet.webp",
    spriteSize: { w: 384, h: 384 },
    animation: {
      key: "anim_boss_snake_fly",
      frames: 8,
      frameRate: 10,
      frameWidth: 256,
      frameHeight: 256,
      repeat: -1,
    },
    body: { shape: "rect", width: 280, height: 110, offsetX: 52, offsetY: 137 },
    movement: {
      action: "serpentine-boss",
      swoopAmplitude: 80,
      swoopPeriodMs: 2800,
      driftAmplitudeX: 60,
      driftPeriodMs: 4200,
      trackSpeed: 0.6,
      secondaryWaveMultiplier: 2.7,
      secondaryWaveAmplitude: 0.3,
    },
    bossPhase: {
      action: "wave-threshold-phases",
      initialBallInteraction: "armored",
      bands: [
        {
          maxWave: 15,
          transitions: [
            {
              phase: 2,
              minShootCooldown: 300,
              shootCooldownMultiplier: 0.66,
              ballInteraction: "shield",
              bossAttackPattern: "pressure",
              tintHex: "#38BDF8",
            },
            {
              phase: 3,
              minShootCooldown: 220,
              shootCooldownMultiplier: 0.56,
              ballInteraction: "normal",
              bossAttackPattern: "climax",
            },
            {
              phase: 4,
              minShootCooldown: 170,
              shootCooldownMultiplier: 0.5,
              ballInteraction: "normal",
              bossAttackPattern: "rage",
              spawnAdds: [{ type: "kamikaze", count: 2 }],
              spawnBumperBurstCount: 1,
            },
          ],
        },
      ],
    },
    role: "Neural Link Serpent",
    movementSummary:
      "A segmented boss representing a corrupted data stream. Splits into head, body, and tail links when fractured.",
    hoverDurationMs: 2000,
    attack: {
      action: "aimed-spread-shot",
      projectileCount: 5,
      spreadDeg: 15,
      damage: 20,
    },
  },
  snakeHead: {
    type: "snakeHead",
    vx: -2.0,
    vy: 0,
    damage: 32,
    points: 120,
    shootCooldown: 650,
    color: "#DC2626",
    hpBase: 200,
    texture: TEX.BOSS_SNAKE_HEAD,
    threatTier: 4,
    assetPath: "enemies/snake/snake-head.webp",
    spriteSize: { w: 120, h: 96 },
    body: { shape: "rect", width: 90, height: 70, offsetX: 15, offsetY: 13 },
    movement: {
      action: "snake-part",
      partRole: "head",
      swoopAmplitude: 110,
      swoopPeriodMs: 2000,
      attackRunSpeed: 7.0,
      attackRunDurationMs: 1100,
      retreatDurationMs: 1800,
      trackSpeed: 2.2,
    },
    role: "Neural Link Head",
    movementSummary: "Dives across the full screen at the player.",
    attack: {
      action: "aimed-spread-shot",
      projectileCount: 3,
      spreadDeg: 8,
      damage: 16,
      projectileSpeedMultiplier: 1.5,
    },
  },
  snakeBody: {
    type: "snakeBody",
    vx: -1.0,
    vy: 0,
    damage: 24,
    points: 100,
    shootCooldown: 1200,
    color: "#A855F7",
    hpBase: 200,
    texture: TEX.BOSS_SNAKE_BODY,
    threatTier: 4,
    assetPath: "enemies/snake/snake-body.webp",
    spriteSize: { w: 110, h: 72 },
    body: { shape: "rect", width: 85, height: 55, offsetX: 12, offsetY: 8 },
    movement: {
      action: "snake-part",
      partRole: "body",
      swoopAmplitude: 140,
      swoopPeriodMs: 2800,
      attackRunSpeed: 2.0,
      attackRunDurationMs: 2200,
      retreatDurationMs: 2800,
      trackSpeed: 0.9,
    },
    ballInteraction: "armored",
    role: "Neural Link Body",
    movementSummary: "Sweeps vertically at mid-field, absorbing shots.",
    attack: {
      action: "aimed-spread-shot",
      projectileCount: 5,
      spreadDeg: 40,
      damage: 10,
    },
  },
  snakeTail: {
    type: "snakeTail",
    vx: -2.8,
    vy: 0,
    damage: 16,
    points: 80,
    shootCooldown: 420,
    color: "#F472B6",
    hpBase: 200,
    texture: TEX.BOSS_SNAKE_TAIL,
    threatTier: 4,
    assetPath: "enemies/snake/snake-tail.webp",
    spriteSize: { w: 96, h: 72 },
    body: { shape: "rect", width: 70, height: 60, offsetX: 13, offsetY: 6 },
    movement: {
      action: "snake-part",
      partRole: "tail",
      swoopAmplitude: 160,
      swoopPeriodMs: 3200,
      attackRunSpeed: 9.0,
      attackRunDurationMs: 600,
      retreatDurationMs: 1200,
      trackSpeed: 2.8,
    },
    role: "Neural Link Tail",
    movementSummary: "Fast stream segment that harries the player.",
    attack: {
      action: "fireball-shot",
      damage: 12,
      projectileSpeedMultiplier: 1.6,
    },
  },
  brain: {
    type: "brain",
    vx: 0,
    vy: 0,
    damage: 60,
    points: 1000,
    shootCooldown: 3000,
    color: "#F43F5E",
    hpBase: 800,
    texture: TEX.BOSS_BRAIN,
    threatTier: 5,
    assetPath: "enemies/boss-brain/shielded-idle.webp",
    spriteSize: { w: 180, h: 180 },
    animation: {
      key: "anim_brain_shielded",
      frames: 8,
      frameRate: 8,
      frameWidth: 1024,
      frameHeight: 1024,
      repeat: -1,
    },
    body: { shape: "rect", width: 200, height: 180, offsetX: 20, offsetY: 30 },
    movement: {
      action: "brain-boss",
      shieldedAmplitude: 30,
      shieldedPeriodMs: 3600,
      exposedAmplitude: 60,
      exposedPeriodMs: 2000,
      trackSpeed: 0.4,
      driftAmplitudeX: 40,
    },
    ballInteraction: "armored",
    role: "Apex System Core",
    movementSummary:
      "Shielded phase absorbs damage; exposed phase is aggressive.",
    hoverDurationMs: 3000,
    attack: {
      action: "aimed-spread-shot",
      projectileCount: 10,
      spreadDeg: 60,
      damage: 30,
    },
    bossPhase: {
      action: "wave-threshold-phases",
      initialBallInteraction: "armored",
      bands: [
        {
          maxWave: Number.POSITIVE_INFINITY,
          transitions: [
            {
              phase: 2,
              minShootCooldown: 400,
              shootCooldownMultiplier: 0.8,
              ballInteraction: "armored",
              bossAttackPattern: "opening",
              spawnAdds: [{ type: "orbDrone", count: 2 }],
            },
            {
              phase: 3,
              minShootCooldown: 280,
              shootCooldownMultiplier: 0.65,
              ballInteraction: "shield",
              bossAttackPattern: "pressure",
              spawnAdds: [{ type: "kamikaze", count: 2 }],
            },
            {
              phase: 4,
              minShootCooldown: 160,
              shootCooldownMultiplier: 0.5,
              ballInteraction: "blocker",
              bossAttackPattern: "climax",
              spawnAdds: [{ type: "kamikaze", count: 2 }],
              spawnBumperBurstCount: 2,
            },
          ],
        },
      ],
    },
  },
  synapseNode: {
    type: "synapseNode",
    vx: 0,
    vy: 0,
    damage: 30,
    points: 250,
    shootCooldown: 2000,
    color: "#06b6d4",
    hpBase: 200,
    texture: TEX.BOSS_SYNAPSE,
    threatTier: 4,
    assetPath: "enemies/brain/synapse-node.webp",
    spriteSize: { w: 64, h: 64 },
    body: { shape: "circle", radius: 28, offsetX: 0, offsetY: 0 },
    movement: {
      action: "hovering",
    },
    role: "Neural Node",
    movementSummary: "Orbits the core, providing absolute protection.",
    ballInteraction: "normal",
    isBossPart: true,
  },
}
